/**
 * Hover treatment shared with the home Hero CTA: a white shine sweeps across,
 * the gradient lightens, the shadow deepens and the button lifts 2px.
 *
 * Returns a CSS string for an inline <style> block. `selectors` must be
 * simple selectors (no commas) — pseudo-elements are appended to each.
 * `transition` is !important so it wins over block buttons that set an
 * inline transition.
 */
export function ctaHoverCss(
  selectors: string | string[],
  color = 'var(--vibe-color-primary)',
): string {
  const list = Array.isArray(selectors) ? selectors : [selectors];
  const sel = (suffix = '') => list.map((s) => `${s}${suffix}`).join(',\n');
  const active = ':not(:disabled)';

  return `
${sel()} {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  transition: all 0.3s ease !important;
}
${sel('::before')} {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
  transition: left 0.5s ease;
}
${sel(`${active}:hover`)} {
  background-image: linear-gradient(135deg, color-mix(in srgb, ${color} 90%, white) 0%, ${color} 100%);
  box-shadow:
    0 4px 12px rgba(0,0,0,0.2),
    0 8px 24px color-mix(in srgb, ${color} 40%, transparent);
  transform: translateY(-2px);
}
${sel(`${active}:hover::before`)} {
  left: 100%;
}
${sel(`${active}:active`)} {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}
${sel(' svg')} {
  transition: transform 0.3s ease;
}
${sel(`${active}:hover svg`)} {
  transform: translateX(0.125rem);
}
`;
}

const ARROW_MASK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3'/%3E%3C/svg%3E")`;

/**
 * Hero CTA arrow drawn as `::after`, for buttons whose markup we can't edit
 * (shared form blocks). Hidden while a button is `aria-busy` (submitting).
 */
export function ctaArrowCss(selectors: string | string[]): string {
  const list = Array.isArray(selectors) ? selectors : [selectors];
  const sel = (suffix = '') => list.map((s) => `${s}${suffix}`).join(',\n');

  return `
${sel()} {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
}
${sel('::after')} {
  content: '';
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  background-color: currentColor;
  -webkit-mask: ${ARROW_MASK} center / contain no-repeat;
  mask: ${ARROW_MASK} center / contain no-repeat;
  transition: transform 0.3s ease;
}
${sel(':not(:disabled):hover::after')} {
  transform: translateX(0.125rem);
}
${sel('[aria-busy="true"]::after')} {
  display: none;
}
`;
}

/**
 * Full home Hero CTA look — gradient pill, resting shadow, uppercase
 * wide-tracked label — plus the shared hover from `ctaHoverCss`.
 * Emit after a component's own button rules so these win on equal specificity.
 */
export function ctaButtonCss(
  selectors: string | string[],
  color = 'var(--vibe-color-primary)',
  textColor = 'var(--vibe-color-text-on-primary, white)',
): string {
  const list = Array.isArray(selectors) ? selectors : [selectors];

  return `
${list.join(',\n')} {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 1rem 2.5rem;
  font-family: var(--vibe-font-body);
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${textColor};
  background: linear-gradient(135deg, ${color} 0%, color-mix(in srgb, ${color} 80%, black) 100%);
  border: none;
  border-radius: 9999px;
  box-shadow:
    0 2px 6px rgba(0,0,0,0.2),
    0 4px 16px color-mix(in srgb, ${color} 30%, transparent);
  cursor: pointer;
}
${ctaHoverCss(list, color)}`;
}
